import { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { UserProfileResponse, ProfileFields } from '@backrs_ui/grow-ui/src/redux/profile/profile';

import { Box, Typography, SxProps, Theme, Button } from '@mui/material';

import { ChecklistEditModal } from './ChecklistEditModal';
import { ChecklistDataOverrides } from '../assessMyTrack.types';
import { ChecklistItem } from '../atoms/ChecklistItem';
import { useAssessmentChecklist } from '../hooks/useAssessmentChecklist';

const ASSESSMENT_CHECKLIST_STRINGS = appStrings.modules.assessMyTrack.assessmentChecklist;

type AssessmentChecklistProps = Readonly<{
  profileData: UserProfileResponse | undefined;
  checklistDataOverrides?: ChecklistDataOverrides;
  title: string;
  description: string;
  variant:
    | {
        type: 'required';
        handleAssessMyTrack: () => void;
        isLoading: boolean;
        allowReassess: boolean;
        hideAssessButton?: boolean;
        hasError?: boolean;
      }
    | {
        type: 'optional';
      };
}>;

export const AssessmentChecklist = ({
  profileData,
  checklistDataOverrides,
  title,
  description,
  variant,
}: AssessmentChecklistProps): ReactElement => {
  const [editingField, setEditingField] = useState<ProfileFields | ProfileFields[] | null>(null);
  const navigate = useNavigate();

  const {
    requiredItems,
    optionalItems,
    progress,
    isChecklistComplete,
    profileOptions,
    isBlueprintComplete,
  } = useAssessmentChecklist(profileData, checklistDataOverrides);

  const isOptional = variant.type === 'optional';
  const checklistItems = isOptional ? optionalItems : requiredItems;

  const handleEditClick = (
    fieldKey: ProfileFields | ProfileFields[] | null,
    customOnEdit?: (navigate: (path: string) => void, doesUserHaveBlueprint: boolean) => void,
  ) => {
    if (customOnEdit) {
      customOnEdit(navigate, isBlueprintComplete);
    } else {
      setEditingField(fieldKey);
    }
  };

  return (
    <Box sx={containerSx}>
      <Box sx={subContainerSx(isOptional ? 'fit-content' : '100%')}>
        <Box sx={titleContainerSx}>
          <Typography variant="text-lg-semibold">{title}</Typography>
          <Typography
            variant="text-sm-regular"
            color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
          >
            {description}
          </Typography>
        </Box>

        {!isOptional && checklistItems.length > 0 && (
          <>
            <Box sx={progressContainerSx}>
              <Box sx={progressBarContainerSx}>
                <Box sx={progressBarSx(progress)} />
              </Box>
              <Typography sx={progressTextSx} variant="text-sm-medium">
                {Math.round(progress)}%
              </Typography>
            </Box>
          </>
        )}

        <Box sx={checklistContainerSx}>
          {checklistItems.map((item, index) => {
            const fieldId = Array.isArray(item.fieldKey)
              ? item.fieldKey.join('-')
              : item.fieldKey || `item-${index}`;

            return (
              <ChecklistItem
                key={index}
                id={`assess-track-checklist-${fieldId}`}
                {...item}
                enrollmentType={item.enrollmentType || undefined}
                disableEditing={!!checklistDataOverrides}
                customOnEdit={item.customOnEdit}
                onClick={handleEditClick}
              />
            );
          })}
        </Box>
        {variant.type === 'required' && !variant.hideAssessButton && (
          <>
            <Button
              hierarchy="Primary"
              size="md"
              onClick={() => variant.handleAssessMyTrack()}
              disabled={!isChecklistComplete || !variant.allowReassess || variant.isLoading}
              sx={buttonSx}
              id="assess-track-assess-button"
            >
              {ASSESSMENT_CHECKLIST_STRINGS.assessMyTrack}
            </Button>
            {variant.hasError && (
              <Typography variant="text-sm-regular" sx={errorTextSx}>
                {ASSESSMENT_CHECKLIST_STRINGS.assessError}
              </Typography>
            )}
          </>
        )}
      </Box>

      {editingField && profileOptions && (
        <ChecklistEditModal
          open={!!editingField}
          onClose={() => setEditingField(null)}
          onSubmit={() => {
            setEditingField(null);
          }}
          fieldKey={editingField}
          profileOptions={profileOptions}
        />
      )}
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  minWidth: 0,
});

const subContainerSx =
  (height: string): SxProps<Theme> =>
  ({ backrs }) => ({
    backgroundColor: backrs.colors['Colors/Base/White'],
    borderRadius: backrs.radius['radius-lg'],
    padding: backrs.spacing['spacing-xl'],
    '@container (min-width: 600px)': {
      padding: backrs.spacing['spacing-3xl'],
    },
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    width: '100%',
    height,
    display: 'flex',
    flexDirection: 'column',
  });

const titleContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const progressContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-lg'],
  alignItems: 'center',
});

const progressTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const progressBarContainerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: backrs.spacing['spacing-md'],
  borderRadius: backrs.radius['radius-md'],
  marginY: backrs.spacing['spacing-2xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  backgroundColor: backrs.colors['Gray/200'],
  overflow: 'hidden',
});

const progressBarSx =
  (progress: number): SxProps<Theme> =>
  ({ backrs }) => ({
    width: `${Math.min(progress, 100)}%`,
    height: '100%',
    backgroundColor: backrs.colors['Colors/Foreground/fg-brand-secondary'],
    borderRadius: backrs.radius['radius-md'],
    transition: 'width 0.3s ease-in-out',
    minWidth: progress > 0 ? '3px' : '0',
  });

const checklistContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  margin: `${backrs.spacing['spacing-lg']} 0`,
  flex: 1,
  overflowY: 'auto',
});

const buttonSx: SxProps<Theme> = {
  width: '100%',
};

const errorTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-error-primary'],
  textAlign: 'center',
});
